// Namespaced global availability partition, BEFORE display pagination.
// All filtered IDs are read in Shopify's selected order; only required native
// card pages are fetched. No inventory changes or synthetic price constraints.
(function () {
  if (window.tfCollectionOrder) return;
  const indexCache = new Map();
  const documentCache = new Map();
  let activeController;
  let generation = 0;
  let state;
  const storageKey = 'tf-collection-index-v2';
  const cacheLifetime = 60000;

  function safeFirstPage(items, pageSize, total) {
    return Array.isArray(items) && items.length > 0 &&
      items.length === Math.min(pageSize, total) &&
      items.every(row => Array.isArray(row) && typeof row[0] === 'string' && row[1] === true);
  }
  function storedIndexes() {
    try {
      const entries = JSON.parse(window.sessionStorage.getItem(storageKey) || '[]');
      return Array.isArray(entries) ? entries.filter(entry => entry &&
        typeof entry.base === 'string' && Date.now() - entry.time >= 0 &&
        Date.now() - entry.time < cacheLifetime && Array.isArray(entry.rows) &&
        entry.rows.length <= 25000 && entry.rows.every((row, index) =>
          Array.isArray(row) && typeof row[0] === 'string' && typeof row[1] === 'boolean' && row[2] === index) &&
        new Set(entry.rows.map(row => row[0])).size === entry.rows.length).slice(-8) : [];
    } catch { return []; }
  }
  function cacheMatches(entry, config) {
    if (!entry || Date.now() - entry.time >= cacheLifetime || entry.rows.length !== config.total) return false;
    if (!Array.isArray(config.nativeItems)) return true;
    const start = (config.page - 1) * config.pageSize;
    return config.nativeItems.every((row, index) =>
      entry.rows[start + index]?.[0] === row[0] && entry.rows[start + index]?.[1] === row[1]);
  }
  function cleanMotion(card) {
    [card, ...card.querySelectorAll('.card')].forEach(node => {
      ['opacity', 'visibility', 'transform', 'translate'].forEach(property => node.style.removeProperty(property));
      if (!node.getAttribute('style')?.trim()) node.removeAttribute('style');
    });
  }

  function orderedRows(rows, stockOnly) {
    const available = rows.filter(row => row[1]);
    return stockOnly ? available : available.concat(rows.filter(row => !row[1]));
  }
  function keyFor(value) {
    const url = new URL(value, window.location.href);
    ['page', 'section_id', 'sections', 'tf_stock_price'].forEach(key => url.searchParams.delete(key));
    url.hash = '';
    url.searchParams.sort();
    return url.toString();
  }
  function pageUrl(value, page, sectionId) {
    const url = new URL(value, window.location.href);
    url.searchParams.set('page', String(page));
    if (sectionId) url.searchParams.set('section_id', sectionId);
    else url.searchParams.delete('section_id');
    return url;
  }
  function remember(cache, key, value, maximum) {
    cache.set(key, value);
    if (cache.size > maximum) cache.delete(cache.keys().next().value);
  }
  async function readDocument(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error('Collection request failed');
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }
  async function workers(values, fn) {
    let position = 0;
    const result = new Array(values.length);
    await Promise.all(Array.from({ length: Math.min(4, values.length) }, async () => {
      while (position < values.length) {
        const current = position++;
        result[current] = await fn(values[current]);
      }
    }));
    return result;
  }
  function parseIndex(doc) {
    const node = doc.querySelector('[data-tf-order-index]');
    if (!node) throw new Error('Collection index unavailable');
    const data = JSON.parse(node.textContent);
    if (!Array.isArray(data.items) || !Number.isInteger(data.pages) || !Number.isInteger(data.total)) {
      throw new Error('Invalid collection index');
    }
    return data;
  }
  async function loadIndex(base, signal, config) {
    const cached = indexCache.get(base) || storedIndexes().find(entry => entry.base === base);
    if (cacheMatches(cached, config)) return cached.rows;
    const first = parseIndex(await readDocument(pageUrl(base, 1, 'tf-collection-order-index'), signal));
    if (first.total > 25000) throw new Error('Collection exceeds Shopify pagination limit');
    const rest = await workers(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => i + 2), async page => {
      const data = parseIndex(await readDocument(pageUrl(base, page, 'tf-collection-order-index'), signal));
      if (data.page !== page || data.total !== first.total) throw new Error('Collection changed during loading');
      return data.items;
    });
    const rows = first.items.concat(...rest).map((row, index) => [row[0], row[1], index]);
    if (rows.length !== first.total || new Set(rows.map(row => row[0])).size !== rows.length) {
      throw new Error('Incomplete collection index');
    }
    const entry = { base, time: Date.now(), rows };
    remember(indexCache, base, entry, 8);
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(
        storedIndexes().filter(item => item.base !== base).concat(entry).slice(-8)));
    } catch { /* Storage may be denied or full; network remains authoritative. */ }
    return rows;
  }
  function cancel() {
    generation++;
    activeController?.abort();
  }
  function scrollToGrid() {
    const target = document.querySelector('.collection');
    if (!target) return;
    const header = document.querySelector('.shopify-section-group-header-group.header-sticky') || document.querySelector('header.header');
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - (header?.getBoundingClientRect().height || 0) - 8,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }
  function pagination(config, base, page, pages) {
    const container = document.createElement('div');
    container.className = 'pagination tf-pg flex flex-wrap justify-center align-self-center md:gap-2 text-sm md:text-base';
    if (pages <= 1) return container;
    function link(number, label, kind, append = false) {
      const anchor = document.createElement('a');
      anchor.href = pageUrl(base, number).toString();
      anchor.dataset.tfOrderPage = String(number);
      if (append) anchor.dataset.tfOrderAppend = '1';
      anchor.className = `${kind} flex items-center justify-center gap-2 font-medium`;
      anchor.textContent = label;
      anchor.setAttribute('aria-label', label);
      container.append(anchor);
    }
    if (config.mode === 'click' || config.mode === 'infinite') {
      if (page < pages) link(page + 1, config.more, 'infinite-scroll button button--primary button--fixed', true);
      return container;
    }
    if (page > 1) link(page - 1, config.previous, 'previous');
    const visible = new Set([1, pages, page - 1, page, page + 1]);
    if (page <= 3) { visible.add(2); visible.add(3); }
    let last = 0;
    Array.from(visible).filter(number => number > 0 && number <= pages).sort((a, b) => a - b).forEach(number => {
      if (last && number > last + 1) {
        const dots = document.createElement('span');
        dots.className = 'page flex items-center justify-center';
        dots.textContent = '…'; container.append(dots);
      }
      if (number === page) {
        const current = document.createElement('span');
        current.className = 'page current flex items-center justify-center rounded-full pointer-events-none';
        current.setAttribute('aria-current', 'page'); current.textContent = String(number); container.append(current);
      } else link(number, String(number), `page rounded-full${number !== 1 && number !== pages && !(page <= 3 && number <= 3) ? ' tf-pg-m-hide' : ''}`);
      last = number;
    });
    if (page < pages) link(page + 1, config.next, 'next');
    return container;
  }
  async function render(value = window.location.href, append = false, suppliedDocument) {
    const container = document.getElementById('ProductGridContainer');
    const configNode = container?.querySelector('[data-tf-order-config]');
    if (!container || !configNode) return;
    const config = JSON.parse(configNode.textContent);
    cancel();
    const ownGeneration = generation;
    activeController = new AbortController();
    const signal = activeController.signal;
    const base = keyFor(value);
    const requested = Math.max(1, Number(new URL(value, window.location.href).searchParams.get('page')) || 1);
    const oldGrid = container.querySelector('motion-list');
    container.removeAttribute('data-tf-order-preview');
    container.setAttribute('aria-busy', 'true');
    if (oldGrid) oldGrid.style.visibility = 'hidden';
    container.querySelector('[data-tf-order-error]')?.remove();
    // A full native first page of purchasable products is already the exact
    // global first page. Reveal only that proven prefix, never a local partition.
    if (oldGrid && requested === 1 && config.page === 1 &&
        (suppliedDocument || !container.dataset.tfOrderReady) &&
        safeFirstPage(config.nativeItems, config.pageSize, config.total)) {
      const nativeCards = [...oldGrid.querySelectorAll('[data-tf-product-id]')];
      if (nativeCards.length === config.nativeItems.length &&
          nativeCards.every((card, index) => card.dataset.tfProductId === config.nativeItems[index][0])) {
        nativeCards.forEach(cleanMotion);
        oldGrid.style.removeProperty('visibility');
        oldGrid.setAttribute('motion-reduced', '');
        oldGrid.motionReduced = true;
        oldGrid.reload?.();
        container.dataset.tfOrderPreview = '1';
      }
    }
    try {
      const rows = await loadIndex(base, signal, config);
      const availability = new URL(base).searchParams.getAll('filter.v.availability');
      const stockOnly = availability.includes('1') && !availability.includes('0');
      const ordered = orderedRows(rows, stockOnly);
      const pages = Math.ceil(ordered.length / config.pageSize);
      const page = Math.min(requested, Math.max(1, pages));
      const wanted = ordered.slice((page - 1) * config.pageSize, page * config.pageSize);
      const sourcePages = [...new Set(wanted.map(row => Math.floor(row[2] / config.pageSize) + 1))];
      const sourceDocs = new Map();
      const currentNativePage = config.page;
      // The current DOM has already been reordered after the first run. Only
      // fresh server markup can be used as a source for native page positions.
      if (suppliedDocument) sourceDocs.set(currentNativePage, suppliedDocument);
      else if (!container.dataset.tfOrderReady) sourceDocs.set(currentNativePage, document);
      await workers(sourcePages, async number => {
        if (sourceDocs.has(number)) return;
        const url = pageUrl(base, number, config.sectionId).toString();
        const cached = documentCache.get(url);
        const doc = cached && Date.now() - cached.time < 60000 ? cached.doc : await readDocument(url, signal);
        remember(documentCache, url, { doc, time: Date.now() }, 40);
        sourceDocs.set(number, doc);
      });
      const cards = new Map();
      sourceDocs.forEach(doc => doc.querySelectorAll('[data-tf-product-id]').forEach(card => cards.set(card.dataset.tfProductId, card)));
      const selectedCards = wanted.map(row => {
        const card = cards.get(row[0]);
        if (!card) throw new Error('Product card missing');
        const clone = card.cloneNode(true);
        // Initial native motion may already have hidden live source cards.
        // Those styles must not travel with a clone: reload only sees cards
        // without inline styles and otherwise leaves a permanent blank slot.
        cleanMotion(clone);
        return clone;
      });
      if (signal.aborted || ownGeneration !== generation) return;
      let grid = container.querySelector('motion-list');
      if (!grid) {
        grid = document.createElement('motion-list');
        grid.className = 'card-grid grid relative';
        container.append(grid);
      }
      const promotions = [...grid.children].filter(node => !node.hasAttribute('data-tf-product-id'));
      if (!append) {
        const fragment = document.createDocumentFragment();
        selectedCards.forEach((card, index) => {
          // Preserve promotion slots from the theme instead of sorting them.
          const promotion = promotions.find(node => Number(node.dataset.tfPromotionPosition) === index + 1);
          if (promotion) fragment.append(promotion);
          fragment.append(card);
        });
        grid.replaceChildren(fragment);
      } else selectedCards.forEach(card => grid.append(card));
      container.querySelector('.empty-state')?.remove();
      if (!ordered.length) {
        const empty = document.createElement('p');
        empty.className = 'empty-state text-center'; empty.textContent = config.empty; grid.append(empty);
      }
      container.querySelectorAll('.pagination').forEach(node => node.remove());
      const controls = pagination(config, base, page, pages);
      container.append(controls);
      const count = document.getElementById('ProductCount');
      if (count) count.textContent = (ordered.length === config.allCount ? config.countSimple : config.countFiltered).replace('__TF_COUNT__', String(ordered.length));
      grid.style.removeProperty('visibility');
      grid.setAttribute('motion-reduced', '');
      // The native element reads this attribute only in its constructor.
      // Update its existing instance too, otherwise reload still staggers cards.
      grid.motionReduced = true;
      grid.reload?.();
      container.dataset.tfOrderReady = '1';
      container.removeAttribute('data-tf-order-preview');
      container.removeAttribute('aria-busy');
      state = { base, page, config };
      if (requested !== page) history.replaceState({}, '', pageUrl(base, page));
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
      if (config.mode === 'infinite') {
        const more = controls.querySelector('[data-tf-order-append]');
        if (more) {
          const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); more.click(); }
          });
          observer.observe(more);
          signal.addEventListener('abort', () => observer.disconnect(), { once: true });
        }
      }
    } catch (error) {
      if (signal.aborted || ownGeneration !== generation) return;
      container.removeAttribute('aria-busy');
      container.removeAttribute('data-tf-order-preview');
      if (oldGrid) oldGrid.style.visibility = 'hidden';
      // Never reveal incorrectly ordered or partially indexed results.
      container.querySelector('.pagination')?.remove();
      const notice = document.createElement('div'); notice.dataset.tfOrderError = '1'; notice.setAttribute('role', 'alert');
      const message = document.createElement('p'); message.textContent = config.error;
      const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'button button--secondary'; retry.textContent = config.retry;
      retry.addEventListener('click', () => render(value, append, suppliedDocument));
      notice.append(message, retry); container.append(notice);
      console.error('TF collection ordering:', error.message);
    }
  }
  window.tfCollectionOrder = { render, cancel, orderedRows, keyFor, safeFirstPage };
  document.addEventListener('click', event => {
    const target = event.target.closest?.('[data-tf-order-page]');
    if (!target || !state) {
      // Native pagination remains visible during the proven-prefix preview,
      // but must not navigate before the global pagination is calculated.
      if (event.target.closest?.('#ProductGridContainer[aria-busy="true"] .pagination a')) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
      return;
    }
    event.preventDefault(); event.stopImmediatePropagation();
    const append = target.dataset.tfOrderAppend === '1';
    const url = pageUrl(state.base, Number(target.dataset.tfOrderPage));
    if (!append) { history.pushState({}, '', url); scrollToGrid(); }
    render(url.toString(), append);
  }, true);
  window.addEventListener('popstate', () => {
    const form = document.querySelector('form[is="facet-form"]');
    if (form?.renderSection) {
      const url = new URL(window.location.href);
      const configNode = document.querySelector('[data-tf-order-config]');
      if (configNode) url.searchParams.set('section_id', JSON.parse(configNode.textContent).sectionId);
      form.renderSection(url.toString());
    } else render();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => render(), { once: true });
  else render();
})();